import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowRight, Database, Plus, Trash2 } from "lucide-react";

const STATUS_COLOR = {
  created: "bg-muted text-foreground",
  uploaded: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  analyzing: "bg-accent/15 text-accent border-accent/30",
  training: "bg-accent/15 text-accent border-accent/30 animate-pulse",
  complete: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  failed: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await endpoints.listProjects();
      setProjects(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, name) => {
    if (!confirm(`Delete project "${name}"?`)) return;
    await endpoints.deleteProject(id);
    toast.success("Project deleted");
    load();
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 max-w-[1400px] mx-auto w-full px-8 py-10">
        <div className="flex items-end justify-between mb-10 border-b border-border pb-6">
          <div>
            <div className="label-mono mb-2">// WORKSPACE</div>
            <h1 className="font-display font-bold text-4xl tracking-tighter">Projects</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-lg">
              Each project owns one dataset, one target and the full pipeline of models trained on it.
            </p>
          </div>
          <Link to="/projects/new" data-testid="dashboard-new-btn">
            <Button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> New run
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm p-16 text-center" data-testid="empty-state">
            <Database className="w-8 h-8 mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
            <div className="font-display font-semibold text-lg">No projects yet</div>
            <p className="text-sm text-muted-foreground mt-1 mb-5">Kick off a run to see it here.</p>
            <Link to="/projects/new">
              <Button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm">
                <Plus className="w-4 h-4 mr-2" /> Create project
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <div
                key={p.id}
                data-testid={`project-card-${p.id}`}
                className="group relative border border-border rounded-sm p-5 bg-card hover:border-accent/40 transition-colors flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <Badge className={`text-[10px] font-mono uppercase tracking-wider ${STATUS_COLOR[p.status] || ""}`}>
                    {p.status}
                  </Badge>
                  <button
                    onClick={() => remove(p.id, p.name)}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                    data-testid={`delete-project-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <Link to={`/projects/${p.id}`} className="block">
                  <div className="font-display font-semibold text-xl mb-1 truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {p.dataset_filename || "no dataset yet"}
                  </div>
                </Link>
                <div className="mt-4 grid grid-cols-3 gap-2 pt-4 border-t border-border">
                  <Stat label="ROWS" value={p.n_rows || "—"} />
                  <Stat label="COLS" value={p.n_cols || "—"} />
                  <Stat label="QUALITY" value={p.quality_score ? `${p.quality_score}` : "—"} />
                </div>
                {p.best_model_name && (
                  <div className="mt-3 text-xs">
                    <span className="text-muted-foreground">Best: </span>
                    <span className="font-mono text-accent">{p.best_model_name}</span>
                  </div>
                )}
                <Link
                  to={`/projects/${p.id}`}
                  className="mt-4 text-xs text-muted-foreground hover:text-accent transition-colors inline-flex items-center gap-1"
                >
                  Open <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="label-mono">{label}</div>
      <div className="num text-sm mt-1">{value}</div>
    </div>
  );
}
